import React from 'react'
import { useOrder } from './hooks/useOrder'
import { OrderTile } from './components/OrderTile'
import {
  Box,
  Col,
  Color,
  Display,
  FlexDirection,
  Grid,
  Heading1,
  Heading4,
  Image,
  Join,
  JustifyContent,
  Link,
  Space,
} from '@gousto-internal/citrus-react'
import { useTracking } from './hooks/useTracking'
import { Notification } from './components/Notification'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { ErrorBoundary } from 'react-error-boundary'
import { useUser } from './hooks/useUser'
import { ControlViewCtas } from './components/ControlViewCtas/ControlViewCtas'
import { ControlViewCtaIntent } from './components/ControlViewCtas/controlViewCtas.data'

// eslint-disable-next-line @typescript-eslint/no-require-imports
const getLoadingGif = () => require(`./assets/Loading-Icon.gif`)

type Props = {
  dispatchCancelOrder: (orderId: string) => void
  dispatchRefreshOrdersState: () => void
  // Only provided for unsubscribed customers; when omitted, the resubscribe/one-off CTAs are hidden.
  onCtaActionClick?: (intent: ControlViewCtaIntent) => void
}

export function MyGoustoPage({
  dispatchCancelOrder,
  dispatchRefreshOrdersState,
  onCtaActionClick,
}: Props) {
  const { dispatchedOrder, pendingOrders, lastDeliveredOrder, isLoading } = useOrder()
  const { user } = useUser()
  const { trackViewAllUpcomingDeliveries } = useTracking()
  const upcomingDeliveries = [...pendingOrders]
  if (dispatchedOrder) {
    upcomingDeliveries.unshift(dispatchedOrder)
  }
  const hasUpcomingDeliveries = upcomingDeliveries.length > 0

  const ctasSection = onCtaActionClick ? (
    <>
      <Box paddingH={[5, 0]}>
        <ControlViewCtas onActionClick={onCtaActionClick} />
      </Box>
      <Space size={5} />
    </>
  ) : null

  const renderOrderTileError = ({ error }: { error: Error }) => {
    const errorMessage = `Failed when rendering the order. ${error}`
    datadogLogs.logger.error(errorMessage)
    datadogRum.addError(error, {
      page: 'MyGoustoPage',
      errorMessage: errorMessage,
    })

    return (
      <Notification
        state="error"
        message="An error occurred when getting your order. Please try again later."
      />
    )
  }

  if (isLoading) {
    return (
      <Grid gap={[6, 8]} paddingV={[4, 8]} paddingH={[0, 3]} bg={Color.NeutralGrey_50}>
        <Col size={12}>
          {ctasSection}
          <Box
            display={Display.Flex}
            justifyContent={JustifyContent.Center}
            data-testid="orders-loader"
          >
            <Image src={getLoadingGif()} width="100" alt="loader" />
          </Box>
        </Col>
      </Grid>
    )
  }

  const greetingMessageTitle = user?.nameFirst ? `Hey ${user.nameFirst}!` : 'Welcome back!'

  if (!hasUpcomingDeliveries && !lastDeliveredOrder) {
    return (
      <Grid gap={[6, 8]} paddingV={[4, 8]} paddingH={[0, 3]} bg={Color.NeutralGrey_50}>
        <Col size={12}>
          <Box>
            <Box paddingRight={[5, 0]} paddingH={[5, 0]}>
              <Heading1 data-experiment-tag="welcome-back">{greetingMessageTitle}</Heading1>
            </Box>
            <Space size={5} />
            {ctasSection}
            <Box
              display={Display.Flex}
              justifyContent={JustifyContent.Center}
              data-testid="orders-loader"
            >
              <Heading4>No orders found</Heading4>
            </Box>
          </Box>
        </Col>
      </Grid>
    )
  }

  return (
    <Grid gap={[6, 8]} paddingV={[4, 8]} paddingH={[0, 3]} bg={Color.NeutralGrey_50}>
      <Col size={12}>
        <Box>
          <Box paddingRight={[5, 0]} paddingH={[5, 0]}>
            <Heading1 data-experiment-tag="greeting-message">{greetingMessageTitle}</Heading1>
          </Box>
          <Space size={5} />
          {ctasSection}
          <Join with={<Space size={4} direction="vertical" />}>
            <Box
              display={Display.Flex}
              flexDirection={FlexDirection.Row}
              justifyContent={JustifyContent.SpaceBetween}
            >
              {hasUpcomingDeliveries && (
                <Box paddingRight={[5, 0]} paddingH={[5, 0]}>
                  <Heading4>Upcoming deliveries</Heading4>
                </Box>
              )}
              <Box paddingRight={[5, 0]}>
                <Link href="/my-deliveries" onClick={trackViewAllUpcomingDeliveries}>
                  View all
                </Link>
              </Box>
            </Box>
            <ErrorBoundary FallbackComponent={renderOrderTileError}>
              {upcomingDeliveries.map((order) => (
                <div key={order.id}>
                  <OrderTile
                    order={order}
                    dispatchCancelOrder={dispatchCancelOrder}
                    dispatchRefreshOrdersState={dispatchRefreshOrdersState}
                  />
                  <Space size={4} />
                </div>
              ))}
            </ErrorBoundary>
            {lastDeliveredOrder && (
              <>
                <Box paddingRight={[5, 0]} paddingH={[5, 0]}>
                  <Heading4>Last delivery</Heading4>
                </Box>
                <Space size={4} />
                <ErrorBoundary FallbackComponent={renderOrderTileError}>
                  <OrderTile
                    order={lastDeliveredOrder}
                    dispatchCancelOrder={dispatchCancelOrder}
                    dispatchRefreshOrdersState={dispatchRefreshOrdersState}
                  />
                </ErrorBoundary>
              </>
            )}
          </Join>
        </Box>
      </Col>
    </Grid>
  )
}
